/**
 * Запросы раздела «Проекты» — `/api/public/projects/*` (US-27 / DP-446).
 *
 * Раздел обслуживают **пять раздельных операций**: реестр фильтруется,
 * ищется и постранично отдаётся, а плашка и оба виджета считаются по всей
 * территории. Одна операция свела бы их к одной выборке, и фильтр
 * «Статус: Завершён» менял бы плашку (US-27 FR-1.1, US-26 FR-3.5).
 *
 * | Операция | Эндпоинт |
 * |---|---|
 * | плашка (US-26 FR-2) | `GET /public/projects/summary` |
 * | реестр (FR-3) | `GET /public/projects/registry` |
 * | карточка (FR-4) | `GET /public/projects/card/{project_uid}` |
 * | АБП (FR-6) | `GET /public/projects/widgets/abp` |
 * | крупнейшие (FR-7) | `GET /public/projects/widgets/largest` |
 *
 * **`work_kind` обязателен** у всех операций, кроме карточки: он
 * переключает ВЫБОРКУ, а не фильтрует полученное. Переключателя «СМР» /
 * «ПСД» на экране нет (US-26 решение 2, FR-3.3), поэтому раздел всегда
 * спрашивает `SMR` — `WORK_KIND`.
 *
 * **Карточка адресуется только `project_uid`**: КАТО берётся из самого
 * проекта, параметров у операции нет.
 *
 * Фильтры, поиск и страница реестра уходят **в запрос**: страница не
 * больше 100 строк, и фильтрация «на экране» показывала бы только первую
 * из них. Плашка и виджеты от фильтров реестра не зависят по построению —
 * это разные операции.
 *
 * Все денежные величины контракта — **в тенге** (US-27 FR-3.1).
 * Ответы прогоняются через нормализаторы: пустое тело / отсутствующие
 * поля / мусор превращаются в безопасные значения (`null` / `[]`).
 *
 * Данных до поставки US-28 (DP-447) нет: операции штатно отвечают
 * `source_connected: false` — это не ошибка и не «строек нет».
 */
import { api } from './api';
import type {
  ContractKindKey,
  InvestmentProject,
  InvestmentProjectsData,
  LocalizedText,
  ProjectCard,
  ProjectContract,
  ProjectMoney,
  ProjectRow,
  ProjectsAbpData,
  ProjectsAbpRow,
  ProjectsBlockHead,
  ProjectsLargestData,
  ProjectsLargestRow,
  ProjectsRegistryData,
  ProjectsRegistryQuery,
  ProjectsSummary,
  ProjectWorkKind,
} from '../types/projects';

/**
 * Вкладка выдачи. Экран показывает проекты БИП и только их, вкладки «ПСД»
 * в новой редакции нет (US-26 решение 2), поэтому значение постоянно.
 */
export const WORK_KIND: ProjectWorkKind = 'SMR';

/** Размер страницы реестра; потолок выдачи — 100 (US-27). */
export const REGISTRY_PAGE_SIZE = 20;

/** Крупнейшие проекты — перечень из пяти по общей стоимости (FR-7.1). */
export const LARGEST_LIMIT = 5;

// --- примитивы нормализации (как в analytics.request.ts) ---

const asObj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const int = (v: unknown, fallback: number): number => num(v) ?? fallback;

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

/** `*_ru` / `*_kz` ответа → LocalizedText; обе версии пустые → null. */
const localized = (
  obj: Record<string, unknown>,
  field: string,
): LocalizedText | null => {
  const ru = str(obj[`${field}_ru`]);
  const kz = str(obj[`${field}_kz`]);
  if (ru === null && kz === null) return null;
  return { ru, kz };
};

/**
 * Признак-разрешение шапки: отсутствие поля в ответе не должно выглядеть
 * как запрет, поэтому умолчание — `true`, выключает только явный `false`.
 */
const flagTrue = (v: unknown): boolean => v !== false;

const moneyFrom = (raw: unknown): ProjectMoney => {
  const e = asObj(raw);
  return {
    totalCost: num(e.total_cost),
    spentBeforeYear: num(e.spent_before_year),
    planYear: num(e.plan_year),
    spentInYear: num(e.spent_in_year),
    spentTotal: num(e.spent_total),
    remainder: num(e.remainder),
    executionPct: num(e.execution_pct),
    annualPlanPct: num(e.annual_plan_pct),
  };
};

/** Шапка, общая для всех пяти операций (US-27, §«Шапка»). */
const headFrom = (raw: unknown): ProjectsBlockHead => {
  const root = asObj(raw);
  const coverage = asObj(root.coverage);
  return {
    kato: num(root.kato),
    katoLevel: str(root.kato_level),
    workKind: str(root.work_kind),
    moduleId: str(root.module_id),
    widgetEnabled: flagTrue(root.widget_enabled),
    levelSupported: flagTrue(root.level_supported),
    sourceConnected: flagTrue(root.source_connected),
    hasData: root.has_data === true,
    reportYear: num(root.report_year),
    requestedYear: num(root.requested_year),
    yearIsRequested: flagTrue(root.year_is_requested),
    actualAt: str(root.actual_at),
    coverage: {
      included: localized(coverage, 'included'),
      excluded: localized(coverage, 'excluded'),
      specifics: str(coverage.specifics),
    },
  };
};

const contractFrom = (entry: unknown): ProjectContract[] => {
  const e = asObj(entry);
  const kind = str(e.kind);
  const name = localized(e, 'name');
  const number = str(e.contract_number);
  // Договор без вида, номера и названия показать нечем.
  if (kind === null && name === null && number === null) return [];
  return [
    {
      contractNumber: number,
      kind: (kind ?? 'OTHER') as ContractKindKey,
      name,
      contractorName: str(e.contractor_name),
      contractorBin: str(e.contractor_bin),
      status: str(e.status),
      money: moneyFrom(e.money),
    },
  ];
};

const projectRowFrom = (entry: unknown): ProjectRow[] => {
  const e = asObj(entry);
  const uid = str(e.project_uid);
  const name = localized(e, 'name');
  // Строка реестра без идентификатора не откроет карточку, без названия —
  // нечего показывать.
  if (uid === null || name === null) return [];
  return [
    {
      projectUid: uid,
      name,
      kato: num(e.kato),
      katoName: localized(e, 'kato_name'),
      workKind: str(e.work_kind),
      projectType: str(e.project_type),
      status: str(e.status),
      abpCode: str(e.abp_code),
      abpName: localized(e, 'abp_name'),
      // Поля пока нет в выдаче DP-446 — по умолчанию признака нет.
      supervisionOnly: e.supervision_only === true,
      money: moneyFrom(e.money),
      contracts: asArr(e.contracts).flatMap(contractFrom),
    },
  ];
};

export const normalizeProjectsSummary = (raw: unknown): ProjectsSummary => {
  const root = asObj(raw);
  return {
    ...headFrom(raw),
    projectCount: int(root.project_count, 0),
    contractCount: int(root.contract_count, 0),
    completedCount: int(root.completed_count, 0),
    money: moneyFrom(root.money),
  };
};

export const normalizeProjectsRegistry = (
  raw: unknown,
): ProjectsRegistryData => {
  const root = asObj(raw);
  const items = asArr(root.items).flatMap(projectRowFrom);
  return {
    ...headFrom(raw),
    page: int(root.page, 1),
    pageSize: int(root.page_size, REGISTRY_PAGE_SIZE),
    shown: int(root.shown, items.length),
    totalFiltered: int(root.total_filtered, items.length),
    totalOnTab: int(root.total_on_tab, items.length),
    statuses: asArr(root.statuses).flatMap((v) => str(v) ?? []),
    items,
  };
};

export const normalizeProjectCard = (raw: unknown): ProjectCard | null => {
  const root = asObj(raw);
  const uid = str(root.project_uid);
  if (uid === null) return null;
  return {
    ...headFrom(raw),
    projectUid: uid,
    name: localized(root, 'name'),
    katoName: localized(root, 'kato_name'),
    projectType: str(root.project_type),
    status: str(root.status),
    startDate: str(root.start_date),
    endDate: str(root.end_date),
    abpCode: str(root.abp_code),
    abpName: localized(root, 'abp_name'),
    budgetProgram: str(root.budget_program),
    budgetSubprogram: str(root.budget_subprogram),
    budgetSpecific: str(root.budget_specific),
    contractorName: str(root.contractor_name),
    contractorBin: str(root.contractor_bin),
    hasPrimaryData: root.has_primary_data === true,
    money: moneyFrom(root.money),
    contracts: asArr(root.contracts).flatMap(contractFrom),
  };
};

const abpRowFrom = (entry: unknown): ProjectsAbpRow[] => {
  const e = asObj(entry);
  const name = localized(e, 'abp_name');
  const code = str(e.abp_code);
  if (name === null && code === null) return [];
  return [
    {
      abpCode: code,
      abpName: name,
      totalCost: num(e.total_cost),
      spentTotal: num(e.spent_total),
      executionPct: num(e.execution_pct),
      projectCount: int(e.project_count, 0),
    },
  ];
};

export const normalizeProjectsAbp = (raw: unknown): ProjectsAbpData => ({
  ...headFrom(raw),
  items: asArr(asObj(raw).items).flatMap(abpRowFrom),
});

const largestRowFrom = (entry: unknown): ProjectsLargestRow[] => {
  const e = asObj(entry);
  const uid = str(e.project_uid);
  const name = localized(e, 'name');
  if (uid === null || name === null) return [];
  return [
    {
      projectUid: uid,
      name,
      kato: num(e.kato),
      katoName: localized(e, 'kato_name'),
      abpCode: str(e.abp_code),
      abpName: localized(e, 'abp_name'),
      totalCost: num(e.total_cost),
      spentTotal: num(e.spent_total),
      executionPct: num(e.execution_pct),
    },
  ];
};

export const normalizeProjectsLargest = (raw: unknown): ProjectsLargestData => ({
  ...headFrom(raw),
  items: asArr(asObj(raw).items).flatMap(largestRowFrom),
});

/**
 * Обязательные параметры операций раздела. Год необязателен: без него
 * отдаётся последний доступный ПО ЭТОЙ ТЕРРИТОРИИ (US-27 FR-1.2).
 */
const blockParams = (
  kato: number,
  year?: number | null,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    kato,
    work_kind: WORK_KIND,
  };
  if (year != null) params.year = year;
  return params;
};

export const getProjectsSummary = async (
  kato: number,
  year?: number | null,
): Promise<ProjectsSummary> => {
  const response = await api.get<unknown>('/public/projects/summary', {
    params: blockParams(kato, year),
  });
  return normalizeProjectsSummary(response.data);
};

export const getProjectsRegistry = async (
  kato: number,
  year: number | null | undefined,
  query: ProjectsRegistryQuery,
): Promise<ProjectsRegistryData> => {
  const params: Record<string, string | number> = {
    ...blockParams(kato, year),
    page: query.page,
    page_size: REGISTRY_PAGE_SIZE,
  };
  // Фильтры применяются совместно, логическим «И» (US-27 FR-1.4); пустой
  // фильтр в запрос не уходит вовсе.
  if (query.status !== null) params.status = query.status;
  if (query.abpCode !== null) params.abp_code = query.abpCode;
  const search = query.search.trim();
  if (search !== '') params.q = search;
  const response = await api.get<unknown>('/public/projects/registry', {
    params,
  });
  return normalizeProjectsRegistry(response.data);
};

/** Карточка адресуется только идентификатором проекта (US-27 FR-1.5). */
export const getProjectCard = async (
  projectUid: string,
): Promise<ProjectCard | null> => {
  const response = await api.get<unknown>(
    `/public/projects/card/${encodeURIComponent(projectUid)}`,
  );
  return normalizeProjectCard(response.data);
};

export const getProjectsAbp = async (
  kato: number,
  year?: number | null,
): Promise<ProjectsAbpData> => {
  const response = await api.get<unknown>('/public/projects/widgets/abp', {
    params: blockParams(kato, year),
  });
  return normalizeProjectsAbp(response.data);
};

export const getProjectsLargest = async (
  kato: number,
  year?: number | null,
): Promise<ProjectsLargestData> => {
  const response = await api.get<unknown>('/public/projects/widgets/largest', {
    params: { ...blockParams(kato, year), limit: LARGEST_LIMIT },
  });
  return normalizeProjectsLargest(response.data);
};

// --- Инвестиционные проекты (US-05.2) ---

const investmentProjectFrom = (
  entry: unknown,
  index: number,
): InvestmentProject[] => {
  const e = asObj(entry);
  const name = localized(e, 'name');
  // Проект без названия — нечего показывать в аккордеоне (FR-2.1).
  if (name === null) return [];
  return [
    {
      id: num(e.id) ?? index,
      name,
      year: num(e.year),
      capacity: localized(e, 'capacity'),
      investmentMln: num(e.investment_mln),
      jobs: num(e.jobs),
      description: localized(e, 'description'),
      effect: localized(e, 'effect'),
      extraFields: asArr(e.extra_fields).flatMap((field) => {
        const f = asObj(field);
        const label = localized(f, 'label');
        const value = localized(f, 'value');
        if (label === null || value === null) return [];
        return [{ label, value }];
      }),
      photos: asArr(e.photos).flatMap((photo) => str(photo) ?? []),
    },
  ];
};

export const normalizeInvestmentProjects = (
  raw: unknown,
): InvestmentProjectsData => {
  const root = asObj(raw);
  return {
    projects: asArr(root.projects).flatMap(investmentProjectFrom),
  };
};

/** Реестр не привязан к отчётному году (FR-6.1) — параметр только `kato`. */
export const getProjectsInvestment = async (
  kato: number,
): Promise<InvestmentProjectsData> => {
  const response = await api.get<unknown>('/public/projects/investment', {
    params: { kato },
  });
  return normalizeInvestmentProjects(response.data);
};
